import React from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SecondaryButton from '../common/SecondaryButton';
import APIService from '../../services/APIService';
import { timeAgo } from '../../common/utils';
import { LIGHT_GRAY, PRIMARY_COLORS } from '../../common/colors';
import { appLink } from '../../common/appLinks';

// "What's happening on OCL" — port of oclweb3's guest-dashboard events
// pattern: public /events/ feed, page number echoed in response headers,
// load-more appends the next page. Entity links deep-link into OCL Online.
const PAGE_SIZE = 5;

const oclOnlineLink = (url) => appLink('termBrowser', url);

const eventSubtitle = (event) => {
  const action = (event.event_type || '').toLowerCase();
  const type = event.referenced_object && event.referenced_object.type;
  if (!type)
    return action;
  return `${action} ${type === 'Organization' ? 'an' : 'a'} ${type.toLowerCase()}`;
};

const EventCard = ({ event }) => {
  const actor = event.object || event.actor || {};
  const referenced = event.referenced_object;
  const highlight = (event.event_type || '').toLowerCase().includes('highlight');
  const getLabel = () => {
    let label = referenced.short_code || referenced.name || referenced.id
    if(referenced.version && referenced.version !== 'HEAD')
      label += `: ${referenced.version}`
    return label
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', border: `1px solid ${LIGHT_GRAY}`, borderRadius: '10px', p: 2 }}>
      <Avatar src={actor.logo_url || undefined} sx={{ width: 44, height: 44, backgroundColor: 'primary.60' }}>
        {highlight ? <CampaignOutlinedIcon /> : (actor.name || actor.username || '?').charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'surface.dark' }}>
          <Box component="a" href={oclOnlineLink(actor.url || '/')} target="_blank" rel="noopener noreferrer" sx={{ color: 'secondary.main', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            {actor.name || actor.username}
          </Box>
          {' '}{eventSubtitle(event)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'surface.light', display: 'block', mb: referenced ? 1 : 0 }}>
          {timeAgo(event.created_at)}
        </Typography>
        {referenced && (
          <Chip
            label={getLabel()}
            size="small"
            component="a"
            href={oclOnlineLink(referenced.version_url || referenced.url)}
            target="_blank"
            rel="noopener noreferrer"
            clickable
            sx={{ backgroundColor: PRIMARY_COLORS['95'], color: 'primary.main', fontWeight: 600, maxWidth: '100%', '&:hover': {backgroundColor: 'primary.80'} }}
          />
        )}
      </Box>
    </Box>
  );
};

const EventSkeletons = () => (
  <>
    {[0, 1, 2].map(i => (
      <Box key={i} sx={{ display: 'flex', gap: 2, border: `1px solid ${LIGHT_GRAY}`, borderRadius: '10px', p: 2 }}>
        <Skeleton variant="circular" width={44} height={44} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="60%" />
          <Skeleton width="30%" />
        </Box>
      </Box>
    ))}
  </>
);

// Always the public community feed, signed in or not. The request goes out
// without the user's token because the API 303s authenticated /events/
// calls to the caller's own feed (and browsers block that redirect after
// a preflighted request).
const EventsStream = () => {
  const { t } = useTranslation();
  const [state, setState] = React.useState({ events: [], page: 0, loading: false, done: false, failed: false });

  const fetchPage = (page) => {
    setState(prev => ({ ...prev, loading: true }));
    APIService.getPublic('/events/', { limit: PAGE_SIZE, page }).then(response => {
      const batch = Array.isArray(response.data) ? response.data : null;
      setState(prev => ({
        events: batch ? [...prev.events, ...batch] : prev.events,
        page,
        loading: false,
        done: !batch || batch.length < PAGE_SIZE,
        failed: !batch && prev.events.length === 0,
      }));
    });
  };

  React.useEffect(() => { fetchPage(1); }, []);

  // A marketing page shouldn't surface API failures (or an empty feed) —
  // hide the section instead.
  if (state.failed || (state.done && !state.loading && state.events.length === 0))
    return null;

  return (
    <Box sx={{ backgroundColor: 'background.paper' }}>
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            {t('events.eyebrow')}
          </Typography>
          <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '2rem', md: '2.5rem' } }}>
            {t('events.heading')}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {state.events.map(event => <EventCard key={event.id} event={event} />)}
          {state.loading && <EventSkeletons />}
        </Box>
        {!state.done && !state.loading && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <SecondaryButton onClick={() => fetchPage(state.page + 1)}>{t('common.load_more')}</SecondaryButton>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default EventsStream;
